/**
 * Configuration encoder/decoder for shareable checkout URLs
 * Encodes form state as Base64 JSON in URL-safe format
 */

/**
 * Encode configuration object to URL-safe Base64 string
 * @param {Object} config - Configuration object
 * @returns {string} URL-safe Base64 encoded string
 */
export function encodeConfig(config) {
  try {
    const jsonString = JSON.stringify(config);
    const base64 = btoa(jsonString);
    // Convert to URL-safe Base64 (replace + with -, / with _, remove padding =)
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch (error) {
    console.error('Error encoding config:', error);
    throw new Error('Failed to encode configuration');
  }
}

/**
 * Decode URL-safe Base64 string to configuration object
 * @param {string} encodedConfig - URL-safe Base64 encoded string
 * @returns {Object|null} Decoded configuration object or null if invalid
 */
export function decodeConfig(encodedConfig) {
  try {
    if (!encodedConfig || typeof encodedConfig !== 'string') {
      return null;
    }

    // Convert from URL-safe Base64 back to standard Base64
    let base64 = encodedConfig.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    while (base64.length % 4) {
      base64 += '=';
    }

    const jsonString = atob(base64);
    const config = JSON.parse(jsonString);
    
    // Validate basic structure
    if (typeof config !== 'object' || config === null) {
      return null;
    }

    return config;
  } catch (error) {
    console.error('Error decoding config:', error);
    return null;
  }
}

/**
 * Validate configuration against available products
 * @param {Object} config - Configuration object
 * @param {Object} productConfig - Product configuration from product-config.js
 * @returns {Object} Validation result with valid config and any warnings
 */
export function validateConfig(config, productConfig) {
  const { primaryProducts, addonSubscriptions, oneTimeProducts } = productConfig;
  const warnings = [];
  const validConfig = {
    primaryPlan: null,
    term: 'monthly',
    addons: [],
    oneTimeProducts: [],
    questionAnswers: {},
    couponCode: null
  };

  // Validate primary plan
  if (config.primaryPlan) {
    const primaryPlan = primaryProducts.find(p => p.id === config.primaryPlan);
    if (primaryPlan) {
      validConfig.primaryPlan = config.primaryPlan;
    } else {
      warnings.push(`Primary plan '${config.primaryPlan}' not found`);
    }
  }

  // Validate term
  if (config.term && ['monthly', 'yearly'].includes(config.term)) {
    validConfig.term = config.term;
  }

  // Validate addons
  if (Array.isArray(config.addons)) {
    config.addons.forEach(addonId => {
      const addon = addonSubscriptions.find(a => a.id === addonId);
      if (addon) {
        validConfig.addons.push(addonId);
      } else {
        warnings.push(`Addon '${addonId}' not found`);
      }
    });
  }

  // Validate one-time products
  if (Array.isArray(config.oneTimeProducts)) {
    config.oneTimeProducts.forEach(productId => {
      const product = oneTimeProducts.find(p => p.id === productId);
      if (product) {
        validConfig.oneTimeProducts.push(productId);
      } else {
        warnings.push(`One-time product '${productId}' not found`);
      }
    });
  }

  // Validate coupon code (basic format check)
  if (config.couponCode && typeof config.couponCode === 'string' && config.couponCode.trim()) {
    validConfig.couponCode = config.couponCode.trim();
  }

  // Validate question answers (basic structure check)
  if (config.questionAnswers && typeof config.questionAnswers === 'object' && config.questionAnswers !== null) {
    validConfig.questionAnswers = config.questionAnswers;
  }

  return {
    config: validConfig,
    warnings
  };
}

/**
 * Generate configuration object from current form state
 * @param {Object} formState - Current form state
 * @returns {Object} Configuration object ready for encoding
 */
export function generateConfigFromState(formState) {
  const {
    selectedPrimaryPlan,
    selectedTerm,
    selectedAddonSubscriptions,
    selectedOneTimeProducts,
    questionAnswers,
    couponCode
  } = formState;

  const config = {
    primaryPlan: selectedPrimaryPlan || null,
    term: selectedTerm || 'monthly',
    addons: [],
    oneTimeProducts: [],
    questionAnswers: questionAnswers || {},
    couponCode: couponCode || null
  };

  // Convert addon subscriptions object to array
  if (selectedAddonSubscriptions) {
    config.addons = Object.keys(selectedAddonSubscriptions)
      .filter(id => selectedAddonSubscriptions[id]);
  }

  // Convert one-time products object to array
  if (selectedOneTimeProducts) {
    config.oneTimeProducts = Object.keys(selectedOneTimeProducts)
      .filter(id => selectedOneTimeProducts[id]);
  }

  return config;
}

/**
 * Apply validated configuration to form state
 * @param {Object} validConfig - Validated configuration object
 * @returns {Object} Form state updates
 */
export function applyConfigToState(validConfig) {
  const stateUpdates = {};

  if (validConfig.primaryPlan) {
    stateUpdates.selectedPrimaryPlan = validConfig.primaryPlan;
  }

  if (validConfig.term) {
    stateUpdates.selectedTerm = validConfig.term;
  }

  // Convert addons array to object
  if (Array.isArray(validConfig.addons)) {
    stateUpdates.selectedAddonSubscriptions = {};
    validConfig.addons.forEach(addonId => {
      stateUpdates.selectedAddonSubscriptions[addonId] = true;
    });
  }

  // Convert one-time products array to object
  if (Array.isArray(validConfig.oneTimeProducts)) {
    stateUpdates.selectedOneTimeProducts = {};
    validConfig.oneTimeProducts.forEach(productId => {
      stateUpdates.selectedOneTimeProducts[productId] = true;
    });
  }

  if (validConfig.couponCode) {
    stateUpdates.couponCode = validConfig.couponCode;
    stateUpdates.showCouponField = true;
  }

  if (validConfig.questionAnswers && typeof validConfig.questionAnswers === 'object') {
    stateUpdates.questionAnswers = validConfig.questionAnswers;
  }

  return stateUpdates;
}
